import { type ProviderInstanceId } from "@cleo/contracts";
import { memo, useId } from "react";
import { CheckIcon, ChevronRightIcon, StarIcon } from "lucide-react";

import { ProviderInstanceIcon } from "./ProviderInstanceIcon";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { type ProviderInstanceEntry, providerInstanceStatusNote } from "../../providerInstances";
import { type ModelPickerProviderGroup } from "./modelPickerPages";

const ROW_CLASS =
  "group relative flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-[background-color,color] hover:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] focus-visible:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] focus-visible:outline-none";
const DISABLED_ROW_CLASS = "cursor-not-allowed opacity-50 hover:bg-transparent";
const SUB_CLASS = "mt-1 truncate text-xs font-normal leading-snug text-muted-foreground/70";

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export interface DrilldownDoor {
  readonly label: string;
  /** Absent when the fix happens elsewhere (e.g. on the Mac that holds the account). */
  readonly onActivate?: (() => void) | undefined;
  /** "locked": the D1 new-session door. "recovery": the fix for a provider that is not ready. */
  readonly kind?: "locked" | "recovery";
  /** The door's action is under way; it cannot be pressed again. */
  readonly busy?: boolean;
}

const DOOR_CLASS =
  "mb-1 ml-8 rounded px-2 py-1 text-left text-xs text-foreground underline-offset-2 hover:bg-muted hover:underline";

/** The action a disabled row's reason names, offered beside the greyed row. */
export function DrilldownDoorControl(props: { door: DrilldownDoor; className?: string }) {
  const { door } = props;
  const marker =
    door.kind === "recovery"
      ? { "data-model-picker-recovery-door": "" }
      : { "data-model-picker-locked-door": "" };
  if (!door.onActivate) {
    return (
      <span
        {...marker}
        className={cn(
          DOOR_CLASS,
          "block text-muted-foreground hover:bg-transparent hover:no-underline",
          props.className,
        )}
      >
        {door.label}
      </span>
    );
  }
  return (
    <button
      type="button"
      {...marker}
      disabled={door.busy}
      className={cn(DOOR_CLASS, door.busy && "cursor-progress opacity-70", props.className)}
      onClick={door.onActivate}
    >
      {door.label}
    </button>
  );
}

function DrilldownRow(props: {
  onActivate: () => void;
  disabledReason?: string | null;
  /** The action a disabled reason names, offered beside the greyed row. */
  disabledDoor?: DrilldownDoor | null;
  selected?: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  /** A sentence under the subtitle that does not disable the row (the Can 21 switch notice). */
  note?: string | null;
  trailing: React.ReactNode;
  /** What the row opens, for the Tester's rail (Can 21): `provider:<driver>` or `instance:<instance id>`. */
  chooserRow?: string;
}) {
  const disabledDescriptionId = useId();
  const row = (
    <button
      type="button"
      className={cn(
        ROW_CLASS,
        props.selected && "bg-foreground/[0.08]",
        props.disabledReason && DISABLED_ROW_CLASS,
      )}
      aria-disabled={Boolean(props.disabledReason)}
      aria-describedby={props.disabledReason ? disabledDescriptionId : undefined}
      {...(props.chooserRow ? { "data-model-chooser-row": props.chooserRow } : {})}
      onClick={() => {
        if (!props.disabledReason) {
          props.onActivate();
        }
      }}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">{props.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium leading-snug">{props.title}</span>
        <span className={SUB_CLASS}>{props.subtitle}</span>
        {props.note ? (
          <span
            className="mt-0.5 block text-xs font-normal leading-snug text-muted-foreground"
            data-model-picker-switch-notice=""
          >
            {props.note}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground/70">
        {props.trailing}
      </span>
    </button>
  );
  if (!props.disabledReason) {
    return row;
  }
  return (
    <>
      <Tooltip>
        <TooltipTrigger render={row} />
        <TooltipPopup side="left" align="center" className="max-w-64 text-balance leading-snug">
          {props.disabledReason}
        </TooltipPopup>
      </Tooltip>
      <span id={disabledDescriptionId} className="sr-only">
        {props.disabledReason}
      </span>
      {props.disabledDoor ? <DrilldownDoorControl door={props.disabledDoor} /> : null}
    </>
  );
}

/**
 * Page 0 — providers grouped by driver kind, favorites seated first.
 * Single-account providers drill straight to their models page (the caller
 * resolves that through `resolveProviderOpenTarget`).
 */
export const ModelPickerProvidersPage = memo(function ModelPickerProvidersPage(props: {
  groups: ReadonlyArray<ModelPickerProviderGroup>;
  favoritesCount: number;
  onOpenFavorites: () => void;
  onOpenProvider: (group: ModelPickerProviderGroup) => void;
  getProviderDisabledReason?: (group: ModelPickerProviderGroup) => string | null;
  /** The door a disabled reason names (the D1 lock's new session), when the surface offers one. */
  getProviderDisabledDoor?: (group: ModelPickerProviderGroup) => DrilldownDoor | null;
  /** A sentence under a choosable provider's label (the Can 21 switch notice). */
  getProviderNote?: (group: ModelPickerProviderGroup) => string | null;
}) {
  return (
    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-1.5">
      <DrilldownRow
        onActivate={props.onOpenFavorites}
        icon={<StarIcon className="size-3.5 fill-current text-yellow-500" />}
        title="Favorites"
        subtitle={
          props.favoritesCount > 0
            ? `${props.favoritesCount} starred`
            : "Star models to pin them here"
        }
        trailing={<ChevronRightIcon className="size-4" />}
      />
      {props.groups.map((group) => {
        const notes = group.entries.map(providerInstanceStatusNote);
        const allUnready = notes.every((note) => note !== null);
        const counts =
          group.entries.length > 1
            ? `${plural(group.entries.length, "account")} · ${plural(group.modelCount, "model")}`
            : plural(group.modelCount, "model");
        // A lone account that is not ready shows why as its visible text; a
        // provider with several opens to its accounts, each with its own note.
        const subtitle =
          group.entries.length === 1 && notes[0]
            ? notes[0]
            : allUnready
              ? `${counts} · unavailable`
              : counts;
        return (
          <DrilldownRow
            key={group.driverKind}
            chooserRow={`provider:${group.driverKind}`}
            onActivate={() => props.onOpenProvider(group)}
            disabledReason={props.getProviderDisabledReason?.(group) ?? null}
            disabledDoor={props.getProviderDisabledDoor?.(group) ?? null}
            note={props.getProviderNote?.(group) ?? null}
            icon={
              <ProviderInstanceIcon
                driverKind={group.driverKind}
                displayName={group.label}
                className="size-4"
                iconClassName="size-4"
              />
            }
            title={group.label}
            subtitle={subtitle}
            trailing={<ChevronRightIcon className="size-4" />}
          />
        );
      })}
    </div>
  );
});

/**
 * Page 1 — the accounts of one provider. Rendered only for multi-account
 * providers; a not-ready account stays on the page, greyed, with its status
 * note on the row and the door that fixes it (F36).
 */
export const ModelPickerAccountsPage = memo(function ModelPickerAccountsPage(props: {
  group: ModelPickerProviderGroup | null;
  selectedInstanceId: ProviderInstanceId | "favorites";
  modelCountOf: (instanceId: ProviderInstanceId) => number;
  onSelectInstance: (instanceId: ProviderInstanceId) => void;
  getInstanceDisabledReason?: (entry: ProviderInstanceEntry) => string | null;
  getInstanceDisabledDoor?: (entry: ProviderInstanceEntry) => DrilldownDoor | null;
  /** A sentence under a choosable account's label (the Can 21 switch notice). */
  getInstanceNote?: (entry: ProviderInstanceEntry) => string | null;
}) {
  if (!props.group) {
    return <div className="min-h-0 flex-1" />;
  }
  return (
    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-1.5">
      {props.group.entries.map((entry) => {
        const statusNote = providerInstanceStatusNote(entry);
        const isSelected = entry.instanceId === props.selectedInstanceId;
        return (
          <DrilldownRow
            key={entry.instanceId}
            chooserRow={`instance:${entry.instanceId}`}
            onActivate={() => props.onSelectInstance(entry.instanceId)}
            disabledReason={props.getInstanceDisabledReason?.(entry) ?? null}
            disabledDoor={props.getInstanceDisabledDoor?.(entry) ?? null}
            note={props.getInstanceNote?.(entry) ?? null}
            selected={isSelected}
            icon={
              <ProviderInstanceIcon
                driverKind={entry.driverKind}
                displayName={entry.displayName}
                accentColor={entry.accentColor}
                showBadge={Boolean(entry.accentColor)}
                className="size-4"
                iconClassName="size-4"
              />
            }
            title={entry.displayName}
            subtitle={statusNote ?? plural(props.modelCountOf(entry.instanceId), "model")}
            trailing={
              isSelected ? (
                <CheckIcon className="size-4 text-foreground" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )
            }
          />
        );
      })}
    </div>
  );
});
